import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { validators } from 'investira.sdk';
import services from '../services';
import withResponseHandling from './withResponseHandling';
import withMessage from './withMessage';
import { OUTPUT } from '../enums';
const PAGE_SIZE = 25;
const withOutput = Component => {
  function WrapComponent(props) {
    const params = useParams();
    const location = useLocation();
    const completed = Boolean(location.state?.completed || new URLSearchParams(location.search).get('completed'));
    const polling = useRef(null);
    const entidadeId = useSelector(state => state.entidade.current.default_entidade_id);
    const carteiras = useSelector(state => state.carteiras.data);
    const [data, setData] = useState({});
    const [isCompleted, setIsCompleted] = useState(false);
    const [isProcessing, setIsProcessing] = useState(true);
    const [isFinished, setIsFinished] = useState(false);
    const [filesCreated, setFilesCreated] = useState(null);
    const [filesErrors, setFilesErros] = useState(null);
    const [filesName, setFilesName] = useState(null);
    const [count, setCount] = useState(0);
    const [caches, setCaches] = useState([]);
    const [includes, setIncludes] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isRefetching, setIsRefetching] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [rowSelection, setRowSelection] = useState({});
    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: PAGE_SIZE
    });
    const [columnFilters, setColumnFilters] = useState([]);
    const [sorting, setSorting] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    function formatColumnFilters(pColumnFilters = []) {
      const xFilters = {};
      pColumnFilters.forEach(xItem => {
        xFilters[xItem.id] = xItem.value;
      });
      return xFilters;
    }
    function formatSorting(pSorting = []) {
      if (validators.isEmpty(pSorting)) {
        return 'created desc';
      }
      const xSorts = {};
      pSorting.forEach(xItem => {
        xSorts[xItem.id] = xItem.desc;
      });
      let xStrSort = ``;
      for (const [key, value] of Object.entries(xSorts)) {
        const xOrder = value === true ? 'DESC' : 'ASC';
        xStrSort = xStrSort + key + ' ' + xOrder;
      }
      return `${xStrSort}, data_posicao`;
    }
    const readCache = useCallback((pParams = {}, pResolve, pReject, pFinally) => {
      const xParams = {
        ...pParams
      };
      services.outputs.cache(xParams, rRes => {
        pResolve && pResolve(rRes);
      }, rErr => {
        pReject && pReject(rErr);
        props.responseErrorHandling(rErr);
      }, () => {
        pFinally && pFinally();
      });
    }, []);
    const readCacheList = useCallback((pParams = {}, pResolve, pReject, pFinally) => {
      if (!caches.length) {
        setIsLoading(true);
      } else {
        setIsRefetching(true);
      }
      setCaches([]);
      const xParams = {
        size: PAGE_SIZE,
        page: 1,
        sort: 'created desc',
        available_filters: true,
        output_id: OUTPUT[params.output].output_id,
        ...pParams
      };
      services.outputs.cacheList(xParams, rRes => {
        setCaches(Object.values(rRes.data));
        setIncludes(rRes.includes || {});
        setRowCount(rRes.pages.total_items);
      }, rErr => {
        props.responseErrorHandling(rErr);
      }, () => {
        setIsLoading(false);
        setIsRefetching(false);
      });
    }, []);
    const deleteCache = (pOutputCacheId, onFinish) => {
      services.outputs.cacheRemove(pOutputCacheId, rRes => {
        props.onMessageSuccess('Relatório excluído com sucesso');
        readCacheList();
        setRowSelection({});
      }, rErr => {
        props.responseErrorHandling(rErr);
      }, () => {
        onFinish && onFinish();
      });
    };
    const bulkDeleteCaches = pRows => {
      const xOutputCacheIds = pRows.map(xRow => {
        const {
          output_cache_id
        } = xRow.original;
        return output_cache_id;
      });
      xOutputCacheIds.forEach(xOutputCacheId => {
        deleteCache(xOutputCacheId);
      });
    };
    useEffect(() => {
      //TODO: Se trocar de entidade, refazer o request e resetar a paginação
      const xPage = pagination.pageIndex + 1;
      const xSort = formatSorting(sorting);
      const xFilters = formatColumnFilters(columnFilters);
      readCacheList({
        page: xPage,
        size: pagination.pageSize,
        ...xFilters,
        sort: xSort,
        output_id: OUTPUT[params.output].output_id
      });
    }, [params.output, readCacheList, pagination.pageIndex, pagination.pageSize, entidadeId, columnFilters, sorting, globalFilter]);
    useEffect(() => {
      setIncludes({});
    }, [params.output]);
    useEffect(() => {
      if (params.id) {
        if (isFinished) {
          clearTimeout(polling.current);
        } else {
          polling.current = setTimeout(() => readCache({
            output_cache_id: params.id,
            response: !completed ? isCompleted : true
          }, rRes => {
            if (validators.isNull(rRes.data)) {
              setIsProcessing(false);
              setIsFinished(true);
            }
            if ((!validators.isNull(rRes.data?.files_created) || !validators.isEmpty(rRes.data?.files_errors)) && completed) {
              setIsProcessing(false);
              setData(rRes.data);
              setFilesCreated(rRes.data?.files_created);
              setFilesErros(rRes.data?.files_errors);
              setFilesName(rRes.data?.files_name);
              setIsFinished(true);
            }
            if (validators.isNull(rRes.data?.files_created) && validators.isEmpty(rRes.data?.files_errors)) {
              setCount(prevState => prevState + 1);
            }
            if (!validators.isNull(rRes.data?.completed)) {
              setIsCompleted(true);
            }
            if (isCompleted && validators.isEmpty(data)) {
              setIsProcessing(false);
              setData(rRes.data);
            }
            if ((!validators.isNull(rRes.data?.files_created) || !validators.isEmpty(rRes.data?.files_errors)) && isCompleted) {
              setFilesCreated(rRes.data.files_created);
              setFilesErros(rRes.data.files_errors);
              setFilesName(rRes.data.files_name);
              setIsFinished(true);
            }
          }), 3000);
        }
      }
      return () => {
        clearTimeout(polling.current);
      };
    }, [count, completed, isCompleted, isProcessing, isFinished, params.id, readCache]);
    const xProps = {
      data,
      completed,
      caches,
      includes,
      carteiras,
      filesName,
      filesErrors,
      filesCreated,
      isProcessing,
      isLoading,
      isRefetching,
      isFinished,
      pagination,
      sorting,
      columnFilters,
      rowCount,
      rowSelection,
      readCacheList,
      setRowSelection,
      setPagination,
      setColumnFilters,
      setSorting,
      setGlobalFilter,
      deleteCache,
      bulkDeleteCaches,
      onMessageError: props.onMessageError
    };
    return <Component {...xProps} />;
  }
  return withMessage(withResponseHandling(WrapComponent));
};
export default withOutput;
